/**
 * Список публичных прокси: форма ответа API и разбор параметров таблицы.
 *
 * Отдельным модулем, а не внутри компонента: одни и те же параметры
 * читает страница (чтобы отрисовать первую выдачу на сервере), route
 * handler (чтобы передать их API) и таблица в браузере. Три разных
 * разбора одной строки запроса разъехались бы молча, и заметить это
 * можно было бы только глазами, сравнивая выдачу до и после перезагрузки.
 *
 * Контракт ручки описан в AI-Tech-API (`internal/transport/httpserver/
 * proxy_handler.go`): фильтры складываются по И, `search` ищет сразу по
 * адресу, порту, стране и городу, а `facets` перечисляют значения,
 * встречающиеся в текущей выборке.
 */

/** Одна запись списка. */
export interface ProxyItem {
  proxy: string;
  protocol: string;
  ip: string;
  /** Ноль у гостя: адрес и порт скрыты до входа. */
  port: number;
  https: boolean;
  anonymity: string;
  score: number;
  /** Время отклика в миллисекундах, измеренное при проверке живости. */
  latency: number;
  geolocation: { country: string; city: string };
}

/** Значение фильтра и сколько раз оно встретилось в выборке. */
export interface ProxyFacet {
  value: string;
  count: number;
}

export interface ProxyFacets {
  protocols: ProxyFacet[];
  countries: ProxyFacet[];
  cities: ProxyFacet[];
  anonymity: ProxyFacet[];
}

/** Ответ ручки `/data/proxy/list`. */
export interface ProxyListResponse {
  items: ProxyItem[];
  /** Сколько записей подошло под фильтры, а не сколько их на странице. */
  total: number;
  limit: number;
  offset: number;
  /** Адреса скрыты и нужен вход. Признак присылает сервер, а не догадка
   *  по звёздочкам: формат маскировки не должен быть закреплён дважды. */
  masked: boolean;
  facets: ProxyFacets;
  /** Когда собран список. Пусто до первого обхода. */
  updated_at?: string;
  /** Идёт ли обход прямо сейчас: «ещё собирается» и «ничего не нашлось»
   *  лечатся разными подсказками. */
  refreshing: boolean;
  /** Сколько записей было в источнике при последнем обходе. */
  checked: number;
}

/** Поля сортировки, которые принимает API. Чужое значение оно отвергает
 *  с 400, поэтому список закрыт и здесь. */
export const PROXY_SORT_FIELDS = ['latency', 'country', 'protocol', 'anonymity'] as const;
export type ProxySortField = (typeof PROXY_SORT_FIELDS)[number];

/** Размер страницы таблицы. */
export const PROXY_PAGE_SIZE = 50;

/**
 * Состояние таблицы: что человек выбрал в фильтрах.
 *
 * Живёт в строке запроса, а не только в памяти компонента: ссылку на
 * «socks5 из Германии быстрее 500 мс» осмысленно отправить или положить
 * в закладки, а страница с потерянными фильтрами после перезагрузки
 * выглядит сломанной.
 */
export interface ProxyQuery {
  search: string;
  protocol: string[];
  countries: string[];
  anonymity: string[];
  /** Не задан — не ограничивать. Разница между «не задан» и «false»
   *  существенна: запрос именно без https осмыслен. */
  https?: boolean;
  /** Верхний предел отклика в мс. Ноль означает «без предела». */
  maxLatency: number;
  sort: ProxySortField;
  desc: boolean;
  offset: number;
}

export const EMPTY_PROXY_QUERY: ProxyQuery = {
  search: '',
  protocol: [],
  countries: [],
  anonymity: [],
  maxLatency: 0,
  sort: 'latency',
  desc: false,
  offset: 0,
};

/** Разбирает список значений через запятую, отбрасывая пустые. */
function splitList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');
}

/**
 * Читает состояние таблицы из строки запроса.
 *
 * Мусор заменяется умолчанием, а не ошибкой: строку запроса правят
 * руками и присылают друг другу обрезанной, и страница, отвечающая на
 * `?sort=score` пустым экраном, помогает меньше, чем страница с
 * обычной сортировкой. Это осознанное отличие от API, где неизвестное
 * поле отвергается: там запрос делает программа, здесь — человек.
 */
export function parseProxyQuery(params: URLSearchParams): ProxyQuery {
  const sortRaw = (params.get('sort') ?? '').toLowerCase();
  const sort = (PROXY_SORT_FIELDS as readonly string[]).includes(sortRaw)
    ? (sortRaw as ProxySortField)
    : 'latency';

  const httpsRaw = params.get('https');
  const https = httpsRaw === 'true' ? true : httpsRaw === 'false' ? false : undefined;

  const maxLatency = Number.parseInt(params.get('max_latency') ?? '', 10);
  const offset = Number.parseInt(params.get('offset') ?? '', 10);

  return {
    search: (params.get('search') ?? '').trim(),
    protocol: splitList(params.get('protocol')),
    countries: splitList(params.get('countries')),
    anonymity: splitList(params.get('anonymity')),
    https,
    maxLatency: Number.isFinite(maxLatency) && maxLatency > 0 ? maxLatency : 0,
    sort,
    desc: params.get('order') === 'desc',
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  };
}

/**
 * Собирает строку запроса из состояния таблицы.
 *
 * Значения по умолчанию не выписываются: адрес страницы без фильтров
 * должен остаться чистым, иначе каждая ссылка тащила бы хвост из
 * `sort=latency&order=asc&offset=0`, а поисковик считал бы её отдельным
 * адресом с тем же содержимым.
 */
export function proxyQueryToParams(query: ProxyQuery, limit = PROXY_PAGE_SIZE): URLSearchParams {
  const params = new URLSearchParams();

  if (query.search !== '') params.set('search', query.search);
  if (query.protocol.length > 0) params.set('protocol', query.protocol.join(','));
  if (query.countries.length > 0) params.set('countries', query.countries.join(','));
  if (query.anonymity.length > 0) params.set('anonymity', query.anonymity.join(','));
  if (query.https !== undefined) params.set('https', String(query.https));
  if (query.maxLatency > 0) params.set('max_latency', String(query.maxLatency));
  if (query.sort !== 'latency') params.set('sort', query.sort);
  if (query.desc) params.set('order', 'desc');
  if (query.offset > 0) params.set('offset', String(query.offset));
  params.set('limit', String(limit));

  return params;
}

/** Строка запроса для адреса страницы: без `limit`, он не выбор человека. */
export function proxyQueryToSearch(query: ProxyQuery): string {
  const params = proxyQueryToParams(query);
  params.delete('limit');
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/** Заданы ли хоть какие-то фильтры: от этого зависит, предлагать ли их сбросить. */
export function hasProxyFilters(query: ProxyQuery): boolean {
  return (
    query.search !== '' ||
    query.protocol.length > 0 ||
    query.countries.length > 0 ||
    query.anonymity.length > 0 ||
    query.https !== undefined ||
    query.maxLatency > 0
  );
}

/**
 * Предел выгрузки: потолок ручки `/data/proxy/list`.
 *
 * Выгружают весь отбор, а не ту страницу, что видно на экране: человек,
 * скачавший пятьдесят адресов из трёхсот отобранных, обнаружит это уже
 * в своей программе.
 */
export const PROXY_EXPORT_LIMIT = 1000;

/**
 * Адрес выгрузки текущего отбора.
 *
 * Отдельной функцией ради теста: тихая ошибка здесь особенно
 * неприятна. Потерянный фильтр даёт файл, который выглядит правильным
 * и открывается без жалоб, а лишние адреса в нём замечают только когда
 * они не подходят к задаче.
 *
 * Смещение намеренно отбрасывается: `offset` вместе с выгрузкой всего
 * отбора означал бы «пропустить первые пятьдесят», то есть молча
 * потерять начало списка у того, кто выгружает со второй страницы.
 */
export function proxyExportUrl(query: ProxyQuery, format: 'txt' | 'csv'): string {
  const params = proxyQueryToParams(query, PROXY_EXPORT_LIMIT);
  params.delete('offset');
  params.set('format', format);
  return `/api/proxy-list?${params.toString()}`;
}
